import { useCallback, useEffect, useState } from "react";
import { SECTIONS } from "./form-config";
import type { FormValues } from "./types";
import { sectionErrors, ageFromDob } from "./lib/validation";
import { loadPersisted, savePersisted, clearPersisted } from "./lib/storage";
import { submitApplication } from "./lib/submit";
import { Welcome } from "./components/Welcome";
import { SectionView } from "./components/SectionView";
import { Completion } from "./components/Completion";
import { ProgressBar } from "./components/ProgressBar";
import { SaveIndicator } from "./components/SaveIndicator";

type Stage = "welcome" | "form" | "complete";

const MIN_AGE = 18;

export function App() {
  const [stage, setStage] = useState<Stage>("welcome");
  const [sectionIndex, setSectionIndex] = useState(0);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState<boolean[]>(() => SECTIONS.map(() => false));
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ageError, setAgeError] = useState<string | null>(null);

  // Restore from localStorage
  useEffect(() => {
    const persisted = loadPersisted();
    if (persisted) {
      setValues(persisted.values);
      setSectionIndex(Math.min(persisted.sectionIndex, SECTIONS.length - 1));
      setSavedAt(persisted.savedAt);
      setHasSaved(true);
      // Recompute completion of past sections
      setCompleted(
        SECTIONS.map((s, i) => {
          if (i > persisted.sectionIndex) return false;
          return Object.keys(sectionErrors(s.fields, persisted.values)).length === 0;
        }),
      );
    }
  }, []);

  // Debounced auto-save
  useEffect(() => {
    if (stage !== "form") return;
    const t = setTimeout(() => {
      const ts = savePersisted(sectionIndex, values);
      setSavedAt(ts);
    }, 700);
    return () => clearTimeout(t);
  }, [values, sectionIndex, stage]);

  const section = SECTIONS[sectionIndex];
  const isLast = sectionIndex === SECTIONS.length - 1;

  const handleChange = useCallback((id: string, value: FormValues[string]) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const { [id]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const focusFirstError = (errs: Record<string, string>) => {
    const firstErroredField = section.fields.find((f) => errs[f.id]);
    if (!firstErroredField) return;
    const el = document.getElementById(`field-${firstErroredField.id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      const focusable = el.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, button.chip, [role="radio"], button[type="button"]',
      );
      focusable?.focus({ preventScroll: true });
    }, 250);
  };

  const handleContinue = () => {
    const errs = sectionErrors(section.fields, values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    setCompleted((prev) => {
      const next = [...prev];
      next[sectionIndex] = true;
      return next;
    });
    if (isLast) {
      handleSubmit();
    } else {
      setSectionIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    if (sectionIndex === 0) {
      setStage("welcome");
      return;
    }
    setSectionIndex((i) => i - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleJump = (i: number) => {
    setSectionIndex(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setAgeError(null);

    const dob = values.mentor_date_of_birth as string | undefined;
    if (dob) {
      const age = ageFromDob(dob);
      if (age !== null && age < MIN_AGE) {
        setAgeError("Sorry — Upside mentors need to be 18 or older.");
        setSectionIndex(0);
        return;
      }
    }

    setSubmitting(true);
    const result = await submitApplication(values);
    setSubmitting(false);
    if (result.ok) {
      clearPersisted();
      setStage("complete");
    } else {
      setSubmitError("We couldn't reach Upside — your answers are saved. Try again?");
    }
  };

  const handleDownload = async () => {
    const { downloadResponsesPdf } = await import("./lib/pdf");
    downloadResponsesPdf(values);
  };

  if (stage === "welcome") {
    return (
      <div className="shell">
        <header className="topbar">
          <a href="https://upside.org.nz" className="logo" aria-label="Upside Youth Mentoring home">
            <img src={`${import.meta.env.BASE_URL}upside-logo.png`} alt="Upside Youth Mentoring" />
          </a>
          <div />
          <div />
        </header>
        <main>
          <Welcome
            onStart={() => setStage("form")}
            hasSaved={hasSaved}
            onClearSaved={() => {
              clearPersisted();
              setValues({});
              setSectionIndex(0);
              setCompleted(SECTIONS.map(() => false));
              setSavedAt(null);
              setHasSaved(false);
            }}
          />
        </main>
      </div>
    );
  }

  if (stage === "complete") {
    return (
      <div className="shell">
        <header className="topbar">
          <a href="https://upside.org.nz" className="logo" aria-label="Upside Youth Mentoring home">
            <img src={`${import.meta.env.BASE_URL}upside-logo.png`} alt="Upside Youth Mentoring" />
          </a>
          <div />
          <div />
        </header>
        <main>
          <Completion onDownload={handleDownload} />
        </main>
      </div>
    );
  }

  return (
    <div className="shell">
      <header className="topbar">
        <a href="https://upside.org.nz" className="logo" aria-label="Upside Youth Mentoring home">
          <img src={`${import.meta.env.BASE_URL}upside-logo.png`} alt="Upside Youth Mentoring" />
        </a>
        <ProgressBar
          total={SECTIONS.length}
          current={sectionIndex}
          completed={completed}
          onJump={handleJump}
        />
        <SaveIndicator savedAt={savedAt} />
      </header>

      <main>
        {ageError && (
          <div className="banner banner-error" role="alert">
            {ageError}
          </div>
        )}
        {submitError && (
          <div className="banner banner-error" role="alert">
            {submitError}
          </div>
        )}

        <SectionView
          index={sectionIndex}
          total={SECTIONS.length}
          section={section}
          values={values}
          errors={errors}
          onChange={handleChange}
        />

      </main>

      <nav className="bottom-bar" aria-label="Form navigation">
        <button className="btn btn-ghost" onClick={handleBack}>
          Back
        </button>
        <button
          className={isLast ? "btn btn-submit" : "btn btn-primary"}
          onClick={handleContinue}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : isLast ? "Submit application" : "Continue"}
        </button>
      </nav>
    </div>
  );
}
